import { useQuery } from '@tanstack/react-query';
import {
  dexTradeKeys,
  DexTradeService,
} from 'api/services/httpServices/DexTradeService';

import { positionStatesStatuses } from '../../../trade-monitor/usePageState';
import {
  DexTradeStatus,
  GetDexOpenPositionsParams,
  OpenPositionListItem,
} from 'api/types/httpsTypes/dex-trade';
import { useOpenTradesSearchFilters } from './usePageParams';
import { TradesComponent } from './trades';
import { useEffect, useState } from 'react';
import { usePositionUpdate } from 'api/hooks/usePositionUpdate';
import { updatePositionsList } from '../../../trade-monitor/components/updatePositionWebsocket';
import { useGlobalChain } from 'modules/routing/chainProvider';
import { useUserState } from 'modules/user/UserContext';
import Button from 'modules/shared-components/button/button';
import { Link } from 'react-router-dom';
import PageTemplate from '../../../components/layout';
import { getAuthLoginUrl } from '../../../../auth/utils';
import { useHistoryTradesSettings } from 'modules/routing/mainSettingsProvider';

export function thDynamicClass(s: string) {
  return s === '' ? 'col-span-1' : '';
}

export function HistoryTradesComponent({
  isTerminal,
}: {
  isTerminal?: boolean;
}) {
  const { filtersState } = useOpenTradesSearchFilters();
  const [selectedChain] = useGlobalChain();
  const [items, setItems] = useState<OpenPositionListItem[]>([]);
  const updatedPosition = usePositionUpdate(selectedChain);
  const { user } = useUserState();

  const { isFailedTradesHistoryHidden } = useHistoryTradesSettings();

  const filteredHistoryStatuses = isFailedTradesHistoryHidden
    ? positionStatesStatuses.history.filter(
        (s) => s !== DexTradeStatus.failedToSnipe
      )
    : positionStatesStatuses.history;

  const params: GetDexOpenPositionsParams = {
    sort: filtersState.sort,
    status: filteredHistoryStatuses,
    limit: filtersState.limit,
    offset: filtersState.offset,
    chain: selectedChain,
    botId: filtersState.botId,
    tokenAddress: filtersState.tokenAddress,
    vaultId: filtersState.vaultId ? [filtersState.vaultId] : undefined,
  };

  const { data, error, isLoading } = useQuery({
    queryKey: dexTradeKeys.getPositions(params),
    queryFn: () => DexTradeService.getPositions(params),
    keepPreviousData: true,
    enabled: !!user,
  });

  useEffect(() => {
    if (data?.data) {
      setItems(data?.data);
    }
  }, [data]);

  const pagination = data?.pagination ?? {
    limit: 0,
    offset: 0,
    total: 0,
  };

  const page = { pagination, data: items };

  // update position with websocket
  useEffect(() => {
    if (!updatedPosition) return;

    setItems((prevItems) => {
      const { updatedItems } = updatePositionsList(
        prevItems,
        updatedPosition,
        false,
        params.tokenAddress,
        params.botId
      );

      return updatedItems;
    });
  }, [updatedPosition]);

  if (!user) {
    return (
      <PageTemplate>
        <div className="flex flex-col justify-center items-center py-24 xxs:mx-4 space-y-2">
          <div className="text-sm font-bold">Login and start trading.</div>
          <Button
            variant="primary"
            as={Link}
            to={getAuthLoginUrl()}
            className=" xxs:px-12 "
            type="button"
          >
            LOGIN
          </Button>
        </div>
      </PageTemplate>
    );
  }

  return (
    <TradesComponent
      isTerminal={isTerminal}
      isOpen={false}
      page={page}
      error={error as boolean | undefined}
      isLoading={isLoading}
    />
  );
}
